import type { ParsedPrayer, Prayer } from '../types/prayer';
import {
  enrichmentTarget,
  materializeProgressEntryIds,
  pendingTokenForEntry,
  progressEntryIds,
} from './progress-entry-identity';

export interface ProgressEntryIdentity {
  id?: string;
  date: string;
  note: string;
}

function entryIndex(prayer: Prayer, identity: ProgressEntryIdentity): number {
  if (identity.id) {
    const ids = progressEntryIds(prayer.id, prayer.progressHistory);
    const exact = ids.indexOf(identity.id);
    if (exact < 0) return -1;
    const entry = prayer.progressHistory[exact];
    return entry.date === identity.date && entry.note === identity.note ? exact : -1;
  }
  return prayer.progressHistory.findIndex(
    (entry) => entry.date === identity.date && entry.note === identity.note,
  );
}

function carriedPendingToken(
  before: Prayer,
  after: Prayer,
  changedEntryId: string,
  updatedAt: string,
) {
  const target = enrichmentTarget(before);
  if (!target || target.entryId === changedEntryId) return null;
  const remainingIds = progressEntryIds(after.id, after.progressHistory);
  return remainingIds.includes(target.entryId)
    ? pendingTokenForEntry(target.entryId, updatedAt)
    : null;
}

function invalidatedParsed(prayer: Prayer): ParsedPrayer | null {
  if (!prayer.userEditedFields?.who || !prayer.parsed) return null;
  return {
    who: prayer.parsed.who,
    core_prayer: '',
    suggestedStatus: null,
    mentionedPeople: [prayer.parsed.who],
    cardLabel: null,
  };
}

function invalidatedStatus(prayer: Prayer): Prayer['status'] {
  if (prayer.status === 'Answered' || prayer.userEditedFields?.status) {
    return prayer.status;
  }
  return 'Open';
}

export function invalidateRawPrayerEdit(
  prayer: Prayer,
  rawText: string,
  updatedAt: string,
): Prayer {
  const materialized = materializeProgressEntryIds(prayer);
  const headId = progressEntryIds(
    materialized.id,
    materialized.progressHistory,
  )[0];
  return {
    ...materialized,
    rawText,
    parsed: invalidatedParsed(materialized),
    gardenSummary: undefined,
    verseResult: null,
    status: invalidatedStatus(materialized),
    pendingEnrichment: headId ? pendingTokenForEntry(headId, updatedAt) : null,
    updatedAt,
  };
}

export function rewriteProgressEntry(
  prayer: Prayer,
  identity: ProgressEntryIdentity,
  note: string,
  updatedAt: string,
): Prayer | null {
  const materialized = materializeProgressEntryIds(prayer);
  const index = entryIndex(materialized, identity);
  if (index < 0) return null;
  const progressHistory = materialized.progressHistory.map((entry, position) =>
    position === index
      ? { ...entry, note, recommendedStage: undefined }
      : entry,
  );
  const changedEntryId = progressEntryIds(materialized.id, progressHistory)[index];
  const pendingEnrichment =
    index === 0
      ? pendingTokenForEntry(changedEntryId, updatedAt)
      : carriedPendingToken(
          materialized,
          { ...materialized, progressHistory },
          changedEntryId,
          updatedAt,
        );
  return {
    ...materialized,
    progressHistory,
    parsed: invalidatedParsed(materialized),
    gardenSummary: undefined,
    status: invalidatedStatus(materialized),
    pendingEnrichment,
    updatedAt,
  };
}

export function removeProgressEntry(
  prayer: Prayer,
  identity: ProgressEntryIdentity,
  updatedAt: string,
): Prayer | null {
  const materialized = materializeProgressEntryIds(prayer);
  const index = entryIndex(materialized, identity);
  if (index < 0) return null;
  const removedId = progressEntryIds(
    materialized.id,
    materialized.progressHistory,
  )[index];
  const progressHistory = materialized.progressHistory.filter(
    (_, position) => position !== index,
  );
  return {
    ...materialized,
    progressHistory,
    parsed: invalidatedParsed(materialized),
    gardenSummary: undefined,
    status: invalidatedStatus(materialized),
    pendingEnrichment: carriedPendingToken(
      materialized,
      { ...materialized, progressHistory },
      removedId,
      updatedAt,
    ),
    updatedAt,
  };
}
